import { LitElement, html, css } from "lit";
import textStyles from '../../common/styles/text-styles';
import variables from '../../common/styles/variables';
import heading from '../../common/headings';
import { customElement, property } from 'lit/decorators.js';

/**
 * This is used to abstract the APIs from ecomm-ui. This component declares it's own JSON interface
 * that other components need to respect, so that when the underlying APIs are changed the interface
 * of this component does not change.
 */
export declare interface ContentCardData {
  kind: any;
  title: string;
  image: string;
  url: string;
  description: string;
}

/**
 * A static element to display content in a card.
 * 
 * @csspart large-card - The container of the large-card variation
 * @csspart small-card - The container of the small-card variation
 * @csspart image - Available in the small-card and large-card variations.
 * @csspart heading - Available in the small-card and large-card variations.
 * @csspart header - Available in the small-card and large-card variations.
 * @csspart details - Available in the large-card variation.
 * @csspart link - Available in the large-card variation.
 */
@customElement('mhecomm-content-card-display')
export class ContentCardDisplay extends LitElement {
  static styles = [variables, textStyles, css`
    :host {
      display: block;
    }

    [part="heading"] {
      color: var(--mh-blue);
      margin: 0;
    }

    [part="image"] {
      grid-area: image;
      display: block;
    }

    [part="header"] {
      text-decoration: none;
      grid-area: header;
    }

    [part="details"] {
      grid-area: details;
    }

    [part="add-to-cart"] {
      grid-area: add-to-cart;
      margin: auto 0 0 auto;
      width: 100%;
    }

    [part="add-to-cart"]::part(button) {
      width: 100%;
    }

    [part="small-card"] {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: var(--mh-spacing-medium);
    }

    [part="small-card"] img {
      height: 64px;
    }

    [part="small-card"] [part="header"] {
      padding-left: var(--mh-spacing-extra-small);
    }

    [part="large-card"] {
      display: grid;
      grid-template-areas: 
            "image header"
            "image details";
      grid-template-rows: auto 1fr;
      grid-template-columns: 1fr 4fr;
      grid-gap: 10px;
      border: 2px solid var(--mh-light-gray);
      padding: var(--mh-spacing-medium);
      margin-bottom: var(--mh-spacing-medium);
    }

    [part="large-card"] img {
      width: 100%;
    }

    @media (max-width: 720px) {
      [part="large-card"] {
        grid-template-areas: 
              "image       header"
              "image       details"
              "add-to-cart add-to-cart";
        grid-template-rows: auto auto auto;
        grid-template-columns: 1fr 3fr;
      }

      [part="add-to-cart"] {
        padding-top: var(--mh-spacing-small);
        border-top: 2px solid var(--mh-light-gray);
      }
    }
  `];

  /**
   * The heading level of the heading text. This should be h1 - h6. 
   * If no value is provided or an improper value is provided, h3 will be used.
   */
  @property()
  headingLevel = 'h3';

  /**
   * The variation of the product card to display in different variations or styles, the values can be small-card, large-card.
   */
  @property()
  variation = 'small-card';

  /**
   * A JSON String in the format of 'ContentCardData'.
   */
  @property({ type: Object })
  content?: ContentCardData;

  @property({ type: Number })
  productIndex?: number;

  render() {
    if (this.variation === 'large-card') {
      return this.largeCard();
    } else {
      return this.smallCard();
    }
  }

  largeCard() {
    return this.content !== undefined ? html`
      <div class="large-card" part="large-card">
        <a part="image" tabindex="-1" href="${this.content.url}">
            <img src="${this.content.image}" />
        </a>
        <a part="header" href="${this.content.url}">
          ${heading(this.headingLevel, this.content.title)}
        </a>
        <div part="details">
          <p>${this.content.description}</p>
        </div>
      </div>
    ` : '';
  }

  smallCard() {
    return this.content !== undefined ? html`
      <div part="small-card">
        <a part="image" tabindex="-1" href="${this.content.url}">
            <img src="${this.content.image}"/>
        </a>
        <a part="header" href="${this.content.url}">
          ${heading(this.headingLevel, this.content.title)}
        </a> 
      </div>
    ` : '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-content-card-display': ContentCardDisplay;
  }
}
